// Small form controls used across the studio panels.
import { useEffect, useId, useState } from 'react';
import { ChevronDown, Minus, Plus } from 'lucide-react';
import { Slider } from '../ui/slider';
import { cn } from '../../lib/utils';
import { FOCUS_RING, TEXT_INPUT } from './constants';
import { pickerValue } from './designUtils';

/** Segmented control: a row of mutually exclusive buttons (aria-pressed). */
export const Segmented = ({ options, value, onChange, label, className, buttonClassName }) => (
  <div
    role="group"
    aria-label={label}
    className={cn('grid gap-[3px] rounded-xl bg-cream p-[3px]', className)}
    style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
  >
    {options.map(([v, text, Icon, aria]) => {
      const on = v === value;
      return (
        <button
          key={String(v)}
          type="button"
          aria-pressed={on}
          aria-label={aria || (Icon ? text : undefined)}
          title={Icon ? text : undefined}
          onClick={() => onChange(v)}
          className={cn(
            'h-8 rounded-[9px] text-xs font-extrabold flex items-center justify-center gap-1 transition-colors',
            on ? 'bg-white text-ink shadow-[0_1px_3px_rgba(0,0,0,0.12)]' : 'text-[#6B665C] hover:text-ink',
            FOCUS_RING,
            buttonClassName
          )}
        >
          {Icon ? <Icon className="h-4 w-4" aria-hidden="true" /> : text}
        </button>
      );
    })}
  </div>
);

/** Toggle switch (role=switch) with a gradient track when on. */
export const Switch = ({ checked, onChange, label, id }) => (
  <button
    id={id}
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    onClick={() => onChange(!checked)}
    className={cn(
      'relative h-6 w-10 shrink-0 rounded-full p-[3px] flex transition-colors',
      checked ? 'bg-logo-gradient justify-end' : 'bg-[#DCD6C8] justify-start',
      FOCUS_RING
    )}
  >
    <span className="h-[18px] w-[18px] rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.25)]" />
  </button>
);

/** A labelled switch row; `children` (sub-controls) show while it's on. */
export const SwitchRow = ({ label, checked, onChange, children }) => (
  <div className="border-b border-[#EEEBE4]">
    <div className="min-h-[44px] flex items-center justify-between gap-3">
      <span className="text-[13px] font-bold text-ink">{label}</span>
      <Switch checked={checked} onChange={onChange} label={label} />
    </div>
    {checked && children ? <div className="pb-3 flex flex-col gap-3">{children}</div> : null}
  </div>
);

/** − value + stepper with an editable number. */
export const Stepper = ({ value, onChange, min = 0, max = 999, step = 1, label, className }) => {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);
  const set = (v) => onChange(Math.min(max, Math.max(min, v)));
  return (
    <div
      className={cn('h-10 rounded-[10px] border border-[#DCD6C8] bg-white flex items-center justify-between px-1', className)}
      role="group"
      aria-label={label}
    >
      <button
        type="button"
        aria-label={`Decrease ${label}`}
        onClick={() => set(Number(value) - step)}
        disabled={value <= min}
        className={cn('h-8 w-8 rounded-full flex items-center justify-center hover:bg-cream disabled:opacity-40', FOCUS_RING)}
      >
        <Minus className="h-4 w-4" aria-hidden="true" />
      </button>
      <input
        type="text"
        inputMode="numeric"
        aria-label={label}
        value={draft}
        onChange={(e) => setDraft(e.target.value.replace(/[^\d.-]/g, ''))}
        onBlur={() => {
          const n = Number(draft);
          if (Number.isFinite(n) && draft !== '') set(Math.round(n));
          else setDraft(String(value));
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
        }}
        className="w-12 text-center text-sm font-extrabold bg-transparent outline-none tabular-nums focus-visible:ring-2 focus-visible:ring-blue/40 rounded"
      />
      <button
        type="button"
        aria-label={`Increase ${label}`}
        onClick={() => set(Number(value) + step)}
        disabled={value >= max}
        className={cn('h-8 w-8 rounded-full flex items-center justify-center hover:bg-cream disabled:opacity-40', FOCUS_RING)}
      >
        <Plus className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
};

const SLIDER_CLASS =
  'py-1.5 [&_.bg-primary]:[background:linear-gradient(120deg,#EC008C,#0CBFF2)] [&_.bg-primary\\/20]:bg-[#E6E1D6] [&_[role=slider]]:h-[18px] [&_[role=slider]]:w-[18px] [&_[role=slider]]:border-2 [&_[role=slider]]:border-ink [&_[role=slider]]:bg-white';

/** Label + value + slider. */
export const SliderRow = ({ label, value, onChange, min, max, step = 1, format = (v) => v, testId }) => (
  <div className="flex flex-col gap-1.5">
    <div className="flex justify-between text-[13px] font-bold text-ink">
      <span>{label}</span>
      <span className="font-semibold text-[#6B665C] tabular-nums">{format(value)}</span>
    </div>
    <Slider
      value={[value]}
      min={min}
      max={max}
      step={step}
      onValueChange={([v]) => onChange(v)}
      aria-label={label}
      className={SLIDER_CLASS}
      data-testid={testId}
    />
  </div>
);

const RAINBOW =
  'conic-gradient(from 90deg, #FF3B30, #FF9500, #FFCC00, #34C759, #00C7BE, #0A84FF, #5E5CE6, #BF5AF2, #FF2D92, #FF3B30)';

/**
 * "Pick any colour" circle: always a multicolour wheel (never the current colour, which the
 * swatches already show); the native colour picker opens on click.
 */
export const CustomColorButton = ({ value, onChange, label = 'Pick any colour', size = 32, testId }) => (
  <label
    title={label}
    className="relative shrink-0 cursor-pointer rounded-full border border-[#DCD6C8] p-[3px] bg-white hover:scale-105 transition-transform focus-within:ring-2 focus-within:ring-blue/50 focus-within:ring-offset-1"
    style={{ width: size, height: size }}
    data-testid={testId}
  >
    <span className="block h-full w-full rounded-full" style={{ background: RAINBOW }} aria-hidden="true" />
    <input
      type="color"
      value={value}
      onChange={onChange}
      aria-label={label}
      className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
    />
  </label>
);

/** Swatches + native colour picker + hex input. */
export const ColorField = ({ label, value, onChange, swatches = [], compact = false }) => {
  const id = useId();
  const [draft, setDraft] = useState(value || '');
  useEffect(() => setDraft(value || ''), [value]);
  const current = (value || '').toLowerCase();
  return (
    <div className="flex flex-col gap-2">
      {label ? (
        <label htmlFor={id} className="text-[13px] font-bold text-ink">
          {label}
        </label>
      ) : null}
      <div className="flex items-center gap-2 flex-wrap">
        {swatches.map(([name, hex]) => {
          const on = current === hex.toLowerCase();
          return (
            <button
              key={hex}
              type="button"
              aria-label={name}
              aria-pressed={on}
              title={name}
              onClick={() => onChange(hex)}
              className={cn(
                'h-7 w-7 rounded-full shrink-0',
                on ? 'ring-2 ring-ink ring-offset-2 ring-offset-white' : 'border border-[#DCD6C8]',
                FOCUS_RING
              )}
              style={{ backgroundColor: hex }}
            />
          );
        })}
        <CustomColorButton value={pickerValue(value)} onChange={(e) => onChange(e.target.value)} label={`${label || 'Colour'}: pick any colour`} />
        {!compact && (
          <input
            id={id}
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(e.target.value)) onChange(e.target.value);
            }}
            spellCheck={false}
            className={cn(TEXT_INPUT, 'h-8 w-[88px] flex-none font-mono text-xs px-2')}
            aria-label={`${label || 'Colour'} hex value`}
          />
        )}
      </div>
    </div>
  );
};

/** Collapsible section with a chevron. */
export const Collapsible = ({ title, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  const id = useId();
  return (
    <div className="border-b border-[#EEEBE4]">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((o) => !o)}
        className={cn('w-full min-h-[44px] flex items-center justify-between text-[13px] font-bold text-ink rounded', FOCUS_RING)}
      >
        {title}
        <ChevronDown className={cn('h-4 w-4 text-[#6B665C] transition-transform', open && 'rotate-180')} aria-hidden="true" />
      </button>
      {open ? (
        <div id={id} className="pb-3 flex flex-col gap-3">
          {children}
        </div>
      ) : null}
    </div>
  );
};

export const PanelHeading = ({ children, className }) => (
  <h2 className={cn('font-serif text-[22px] font-semibold text-ink leading-tight', className)}>{children}</h2>
);
